// Font loading and management service

import { FontParityManager, FontDescriptor } from './FontParityManager';

export interface FontLoadingConfig {
  harfbuzzFontEndpoint: string; // Backend endpoint for font files
  fallbackFonts: string[];
  enableWebFontLoading: boolean;
  fontDisplayStrategy: 'block' | 'swap' | 'fallback' | 'optional';
}

export class FontLoadingService {
  private config: FontLoadingConfig;
  private loadingPromises = new Map<string, Promise<boolean>>();

  constructor(config: FontLoadingConfig) {
    this.config = config;
  }

  /**
   * Ensure font is loaded and ready for rendering
   */
  async ensureFontReady(
    backendFont: FontDescriptor,
    requestedFont: { family: string; size: number }
  ): Promise<{
    fontFamily: string;
    isExactMatch: boolean;
    loadingMethod: 'webfont' | 'system' | 'fallback';
  }> {
    // Check font parity
    const parityResult = await FontParityManager.verifyFontParity(backendFont, requestedFont);

    if (parityResult.isMatch) {
      return {
        fontFamily: backendFont.family,
        isExactMatch: true,
        loadingMethod: 'system'
      };
    }

    // Try to load exact font from backend
    if (this.config.enableWebFontLoading && backendFont.hash) {
      const fontUrl = `${this.config.harfbuzzFontEndpoint}/fonts/${backendFont.hash}`;
      const loadingKey = `${backendFont.family}_${backendFont.hash}`;

      // Check if already loading
      if (!this.loadingPromises.has(loadingKey)) {
        this.loadingPromises.set(
          loadingKey,
          FontParityManager.loadBackendFont(backendFont, fontUrl)
        );
      }

      const loadSuccess = await this.loadingPromises.get(loadingKey)!;

      if (loadSuccess) {
        const exactFontFamily = FontParityManager.getFontFamilyForRendering(backendFont, parityResult);
        return {
          fontFamily: exactFontFamily,
          isExactMatch: true,
          loadingMethod: 'webfont'
        };
      }
    }

    // Fall back to system font or fallback
    const fallbackFont = this.selectBestFallback(backendFont);
    
    console.warn(`⚠️ [FontLoadingService] Using fallback font "${fallbackFont}" for "${backendFont.family}"`, {
      differences: parityResult.differences,
      recommendation: parityResult.recommendation
    });

    return {
      fontFamily: fallbackFont,
      isExactMatch: false,
      loadingMethod: 'fallback'
    };
  }

  /**
   * Select best fallback font
   */
  private selectBestFallback(targetFont: FontDescriptor): string {
    // Try requested font first
    if (this.isFontAvailable(targetFont.family)) {
      return targetFont.family;
    }

    // Try configured fallbacks
    for (const fallback of this.config.fallbackFonts) {
      if (this.isFontAvailable(fallback)) {
        return fallback;
      }
    }

    // Generic fallbacks based on font characteristics
    if (targetFont.family.toLowerCase().includes('serif')) {
      return 'serif';
    } else if (targetFont.family.toLowerCase().includes('mono')) {
      return 'monospace';
    } else {
      return 'sans-serif';
    }
  }

  /**
   * Check if font is available in the system
   */
  private isFontAvailable(fontFamily: string): boolean {
    const testCanvas = document.createElement('canvas');
    const testCtx = testCanvas.getContext('2d')!;

    // Test with a known different font
    testCtx.font = '12px monospace';
    const baselineWidth = testCtx.measureText('mmmmmmmmmmlli').width;

    // Test with requested font
    testCtx.font = `12px "${fontFamily}", monospace`;
    const testWidth = testCtx.measureText('mmmmmmmmmmlli').width;

    return Math.abs(baselineWidth - testWidth) > 0.1;
  }

  /**
   * Preload fonts for better performance
   */
  async preloadFonts(fonts: FontDescriptor[]): Promise<void> {
    const loadPromises = fonts.map(font => 
      this.ensureFontReady(font, { family: font.family, size: 12 })
    );

    await Promise.allSettled(loadPromises);
    console.log(`✅ [FontLoadingService] Preloaded ${fonts.length} fonts`);
  }
}
